import type {
  Anchor,
  Locale,
  Mode,
  OpenContext,
  StreamProtocol,
  ThemeMode,
  WidgetOptions,
} from "../types.ts";
import { bool, json, num } from "./util.ts";

export interface ResolvedConfig extends Required<
  Pick<
    WidgetOptions,
    | "apiBase"
    | "mode"
    | "anchor"
    | "width"
    | "zIndex"
    | "headerHeight"
    | "theme"
    | "locale"
    | "launcher"
    | "autoOpen"
    | "maxLength"
    | "timeout"
    | "streaming"
    | "persistSession"
    | "historyLimit"
    | "markdown"
    | "voice"
    | "fetchSession"
    | "debug"
    | "credentials"
    | "streamProtocol"
    | "size"
    | "autoExpand"
  >
> {
  target: string | HTMLElement | null;
  backend: WidgetOptions["backend"];
  tokens: Record<string, string>;
  headers: Record<string, string>;
  client?: string;
  voiceAutoSend?: boolean;
  endpoints: { chat: string; session: string };
  getToken: WidgetOptions["getToken"] | null;
  context: OpenContext | null;
  title?: string;
  subtitle?: string;
  placeholder?: string;
  avatarUrl?: string;
  mascotUrl?: string;
  metadata?: Record<string, unknown>;
  onReady?: WidgetOptions["onReady"];
  onOpen?: WidgetOptions["onOpen"];
  onClose?: WidgetOptions["onClose"];
  onMessage?: WidgetOptions["onMessage"];
  onAction?: WidgetOptions["onAction"];
  onError?: WidgetOptions["onError"];
}

export const DEFAULTS: ResolvedConfig = {
  apiBase: "",
  backend: null,
  size: "compact",
  autoExpand: true,
  /* Apus Ami: mặc định là bong bóng góc phải dưới — ERP chỉ cần dán script, không phải
     làm nút hay chừa chỗ. App muốn panel bám cạnh phải thì đặt mode "dock". */
  mode: "float",
  target: null,
  anchor: "right",
  width: 400,
  zIndex: 2147483000,
  headerHeight: 60,
  theme: "auto",
  tokens: {},
  locale: "vi",
  launcher: true,
  autoOpen: false,
  context: null,
  /* Trần độ dài câu hỏi — mục 6.2 của tài liệu phạm vi. */
  maxLength: 1000,
  /* Ngưỡng nghiệm thu C3: quá 20 giây thì báo lỗi và không tính lượt. */
  /* Apus Ami: nghiên cứu thị trường trên internet và soạn báo cáo có thể mất 30–90 giây. */
  timeout: 120000,
  streaming: "auto",
  streamProtocol: "assistant-v1",
  /* Cache nhanh trong tab; khi mở widget, server trả bản lịch sử chính thức. */
  persistSession: true,
  historyLimit: 100,
  headers: {},
  credentials: "same-origin",
  getToken: null,
  markdown: true,
  voice: true,
  fetchSession: true,
  debug: false,
  endpoints: {
    chat: "/api/v1/assistant/chat",
    session: "/api/v1/assistant/session",
  },
};

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === "string" && (allowed as readonly string[]).includes(value) ? (value as T) : undefined;
}

/**
 * Đọc cấu hình từ thuộc tính data-* của thẻ <script>. Đường này dành cho các
 * bề mặt không dùng bundler; trong SPA chính thì gọi `init()` để truyền được
 * `getToken` và `onAction` — hai thứ không diễn đạt được bằng chuỗi.
 */
export function fromScript(script: HTMLScriptElement | null): Partial<WidgetOptions> {
  if (!script) return {};
  const d = script.dataset;
  const out: Partial<WidgetOptions> = {};

  if (d.apiBase) out.apiBase = d.apiBase;
  if (d.client) out.client = d.client.trim();
  const autoSend = bool(d.voiceAutoSend);
  if (autoSend !== undefined) out.voiceAutoSend = autoSend;
  if (d.target) out.target = d.target;
  if (d.backend) out.backend = d.backend;
  if (d.title) out.title = d.title;
  if (d.subtitle) out.subtitle = d.subtitle;
  if (d.placeholder) out.placeholder = d.placeholder;
  if (d.avatarUrl) out.avatarUrl = d.avatarUrl;
  if (d.mascotUrl) out.mascotUrl = d.mascotUrl;

  const mode = oneOf<Mode>(d.mode, ["dock", "inline", "float"]);
  if (mode) out.mode = mode;
  const size = oneOf<"compact" | "wide" | "full">(d.size, ["compact", "wide", "full"]);
  if (size) out.size = size;
  const anchor = oneOf<Anchor>(d.anchor, ["right", "left"]);
  if (anchor) out.anchor = anchor;
  const theme = oneOf<ThemeMode>(d.theme, ["light", "dark", "auto"]);
  if (theme) out.theme = theme;
  const locale = oneOf<Locale>(d.locale, ["vi", "en"]);
  if (locale) out.locale = locale;
  const streamProtocol = oneOf<StreamProtocol>(d.streamProtocol, ["assistant-v1", "qonnex-agent"]);
  if (streamProtocol) out.streamProtocol = streamProtocol;

  const numbers: Array<[keyof WidgetOptions, string | undefined]> = [
    ["width", d.width],
    ["zIndex", d.zIndex],
    ["headerHeight", d.headerHeight],
    ["timeout", d.timeout],
    ["maxLength", d.maxLength],
    ["historyLimit", d.historyLimit],
  ];
  for (const [key, raw] of numbers) {
    const parsed = num(raw);
    if (parsed !== undefined) (out as Record<string, unknown>)[key as string] = parsed;
  }

  const booleans: Array<[keyof WidgetOptions, string | undefined]> = [
    ["launcher", d.launcher],
    ["persistSession", d.persistSession],
    ["markdown", d.markdown],
    ["voice", d.voice],
    ["fetchSession", d.fetchSession],
    ["debug", d.debug],
  ];
  for (const [key, raw] of booleans) {
    const parsed = bool(raw);
    if (parsed !== undefined) (out as Record<string, unknown>)[key as string] = parsed;
  }

  if (d.streaming === "auto") out.streaming = "auto";
  else {
    const streaming = bool(d.streaming);
    if (streaming !== undefined) out.streaming = streaming;
  }

  const autoOpenDelay = num(d.autoOpen);
  if (autoOpenDelay !== undefined) out.autoOpen = autoOpenDelay;
  else {
    const autoOpen = bool(d.autoOpen);
    if (autoOpen !== undefined) out.autoOpen = autoOpen;
  }

  /* Trường phức tạp đi qua JSON. Sai cú pháp thì bỏ qua chứ không nổ. */
  const tokens = json<Record<string, string>>(d.tokens);
  if (tokens) out.tokens = tokens;
  const headers = json<Record<string, string>>(d.headers);
  if (headers) out.headers = headers;
  const metadata = json<Record<string, unknown>>(d.metadata);
  if (metadata) out.metadata = metadata;
  const context = json<OpenContext>(d.context);
  if (context) out.context = context;
  const endpoints = json<Record<string, string>>(d.endpoints);
  if (endpoints) out.endpoints = endpoints;

  const shorthand: Record<string, string | undefined> = {
    brand: d.brand,
    grad: d.gradient,
    primary: d.primary,
  };
  for (const key of Object.keys(shorthand)) {
    const value = shorthand[key];
    if (value) out.tokens = { ...(out.tokens || {}), [key]: value };
  }

  return out;
}

/** Header gửi kèm mọi request: `headers` của app chủ + `X-Apus-Client` khi có mã khách. */
export function requestHeaders(config: { headers: Record<string, string>; client?: string }): Record<string, string> {
  const client = (config.client || "").trim();
  return client ? { ...config.headers, "X-Apus-Client": client } : config.headers;
}

export function normalizeTokens(tokens: Record<string, string> | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!tokens) return out;
  for (const key of Object.keys(tokens)) {
    const value = tokens[key];
    if (typeof value !== "string") continue;
    /* Nhận cả "brand" lẫn "--apx-brand" để khỏi phải nhớ tiền tố. */
    const name = key.startsWith("--") ? key : "--apx-" + key.replace(/^apx-/, "");
    out[name] = value;
  }
  return out;
}
